import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CardMedia,
  Skeleton,
  Stack,
} from "@mui/material";
import {
  Add,
  Close,
  MoreVert,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  AttachFile,
  Inventory2,
  ArrowBackIos,
  ArrowForwardIos,
  Inventory,
  RemoveShoppingCart,
} from "@mui/icons-material";
import { URL as API_URL } from "../constants/url"; // Renamed to avoid conflict
import colors from "../constants/colors";

// Skeleton Product Card Component
const SkeletonProductCard = () => (
  <Card
    sx={{
      width: 280,
      height: 380,
      mx: "auto",
      borderRadius: 4,
      border: "1px solid rgba(128, 128, 128, 0.2)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      backgroundColor: colors.white,
    }}
  >
    {/* Image Skeleton */}
    <Box
      sx={{
        width: "90%",
        height: 180,
        mt: 2,
        mx: "auto",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#f0f0f0",
      }}
    >
      <Skeleton variant="rectangular" width="100%" height="100%" />
    </Box>

    {/* Card Body Skeleton */}
    <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
      {/* Title Skeleton */}
      <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />

      {/* Description Skeleton */}
      <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
      <Skeleton variant="text" width="70%" height={20} sx={{ mb: 2 }} />

      {/* Price and Stock Row Skeleton */}
      <Box sx={{ flexGrow: 1 }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Skeleton variant="text" width={80} height={30} />
        <Skeleton variant="rounded" width={100} height={24} />
      </Box>

      {/* Action Button Skeleton */}
      <Skeleton variant="rounded" width="100%" height={36} />
    </Box>
  </Card>
);

const ShopProducts = ({ adminData, searchText }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageIndex, setImageIndex] = useState({});
  const [imagePreviews, setImagePreviews] = useState([]); // For new image previews
  const [editImagePreviews, setEditImagePreviews] = useState([]); // For edit image previews

  const ITEMS_PER_PAGE = 9;
  const productsContainerRef = useRef(null);

  // Scroll to top when page changes
  useEffect(() => {
    if (productsContainerRef.current) {
      productsContainerRef.current.scrollTop = 0;
    }
  }, [page]);

  // Reset to page 1 when search text changes
  useEffect(() => {
    if (searchText) {
      setPage(1);
    }
  }, [searchText]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    offerPrice: "",
    stock: "",
    images: [],
  });

  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    price: "",
    offerPrice: "",
    stock: "",
    images: [],
    removedImages: [], // Track existing image paths to remove
  });



  // Filter products based on search text
  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (searchText && searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.title?.toLowerCase().includes(searchLower) ||
          product.description?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [products, searchText]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, page]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  }, [filteredProducts]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (searchText && searchText.trim() !== "") {
        queryParams.append("keyword", searchText);
      }
      queryParams.append("page", page);
      queryParams.append("limit", ITEMS_PER_PAGE);
      queryParams.append("isAdmin", "true"); // Add this to show all products including out of stock

      const res = await fetch(
        `${API_URL}/api/shop/fetch-products?${queryParams}`
      );
      const data = await res.json();

      console.log("Fetched products:", data.products);

      if (data.success) {
        setProducts(data.products || []);
        setTotalPages(data.totalPages || 1);
      } else {
        throw new Error(data.error || "Failed to fetch products");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      showAlert("Error fetching products", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [searchText, page]);

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(() => {
      setAlert({ open: false, message: "", severity: "success" });
    }, 4000);
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "success" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));

    // Create previews for new images
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const handleEditImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setEditFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files], // Add to existing images
    }));

    // Create previews for new edit images
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setEditImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    // Also remove the preview URL
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const removeEditImage = (index) => {
    setEditFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    // Also remove the preview URL
    setEditImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNextImage = (id, total) => {
    setImageIndex((prev) => ({
      ...prev,
      [id]: ((prev[id] || 0) + 1) % total,
    }));
  };

  const handlePrevImage = (id, total) => {
    setImageIndex((prev) => ({
      ...prev,
      [id]: ((prev[id] || 0) - 1 + total) % total,
    }));
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      price: "",
      offerPrice: "",
      stock: "",
      images: [],
    });
    // Revoke object URLs to prevent memory leaks
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setImagePreviews([]);
  };

  const resetEditForm = () => {
    setEditFormData({
      title: "",
      description: "",
      price: "",
      offerPrice: "",
      stock: "",
      images: [],
      removedImages: [],
    });
    // Revoke object URLs to prevent memory leaks
    editImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setEditImagePreviews([]);
    setEditingProduct(null);
  };

  const removeExistingImage = (imagePath) => {
    setEditFormData(prev => ({
      ...prev,
      removedImages: [...prev.removedImages, imagePath]
    }));
    // Remove from temporary editingProduct state for UI update
    setEditingProduct(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== imagePath)
    }));
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
      editImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogLoading(true);

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        showAlert("Product title is required", "error");
        setDialogLoading(false);
        return;
      }
      if (!formData.price || formData.price <= 0) {
        showAlert("Valid price is required", "error");
        setDialogLoading(false);
        return;
      }
      if (!formData.stock || formData.stock < 0) {
        showAlert("Valid stock quantity is required", "error");
        setDialogLoading(false);
        return;
      }

      const submitData = new FormData();
      submitData.append("title", formData.title.trim());
      submitData.append("description", formData.description.trim());
      submitData.append("price", formData.price);
      submitData.append("offerPrice", formData.offerPrice || formData.price);
      submitData.append("stock", formData.stock);

      console.log("Submitting product data:", {
        title: formData.title,
        description: formData.description,
        price: formData.price,
        offerPrice: formData.offerPrice,
        stock: formData.stock,
        imagesCount: formData.images.length
      });

      formData.images.forEach((image, index) => {
        console.log(`Appending image ${index}:`, image.name, image.type);
        submitData.append("shopProductImages", image);
      });

      const res = await fetch(`${API_URL}/api/shop/create-product`, {
        method: "POST",
        body: submitData,
      });

      console.log("Response status:", res.status);
      const result = await res.json();
      console.log("Response data:", result);
      console.log("Full error details:", JSON.stringify(result.error, null, 2));

      if (res.ok && result.message === "Product created successfully") {
        setOpenDialog(false);
        resetForm();
        showAlert("Product created successfully!");
        fetchProducts();
      } else {
        console.error("Backend error:", result);
        const errorMessage = result.error?.message || result.error || result.message || "Error creating product";
        showAlert(errorMessage, "error");
      }
    } catch (error) {
      console.error("Network/Client error:", error);
      showAlert(`Error creating product: ${error.message}`, "error");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setDialogLoading(true);

    try {
      const submitData = new FormData();

      console.log("Edit form data:", editFormData);
      console.log("Editing product:", editingProduct);

      // Only append fields that have values (partial update)
      if (editFormData.title && editFormData.title.trim()) {
        submitData.append("title", editFormData.title.trim());
      }
      if (editFormData.description !== undefined) {
        submitData.append("description", editFormData.description.trim());
      }
      if (editFormData.price) {
        submitData.append("price", editFormData.price);
      }
      if (editFormData.offerPrice) {
        submitData.append("offerPrice", editFormData.offerPrice);
      }
      if (editFormData.stock) {
        submitData.append("stock", editFormData.stock);
      }

      // Append new images only if provided
      editFormData.images.forEach((image, index) => {
        if (image instanceof File) {
          console.log(`Appending edit image ${index}:`, image.name, image.type);
          submitData.append("shopProductImages", image);
        }
      });

      // Append removed images paths
      if (editFormData.removedImages.length > 0) {
        submitData.append("removedImages", JSON.stringify(editFormData.removedImages));
      }

      console.log("Submitting edit data for product:", editingProduct._id);

      const res = await fetch(
        `${API_URL}/api/shop/edit-product/${editingProduct._id}`,
        {
          method: "PUT",
          body: submitData,
        }
      );

      console.log("Edit response status:", res.status);
      const result = await res.json();
      console.log("Edit response data:", result);

      if (res.ok && result.message === "Product updated successfully") {
        setEditDialogOpen(false);
        resetEditForm();
        showAlert("Product updated successfully!");
        fetchProducts();
      } else {
        console.error("Backend edit error:", result);
        const errorMessage = result.error?.message || result.error || result.message || "Error updating product";
        showAlert(errorMessage, "error");
      }
    } catch (error) {
      console.error("Network/Client edit error:", error);
      showAlert(`Error updating product: ${error.message}`, "error");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct || !selectedProduct._id) {
      showAlert("❌ Error: No product selected for deletion", "error");
      setDeleteDialogOpen(false);
      setDialogLoading(false);
      return;
    }

    setDialogLoading(true);
    try {
      const productIdToDelete = selectedProduct._id;
      const deleteUrl = `${API_URL}/api/shop/delete-product/${productIdToDelete}`;

      const res = await fetch(deleteUrl, {
        method: "DELETE",
      });

      const result = await res.json();

      if (result.message === "Product deleted successfully") {
        setDeleteDialogOpen(false);
        setMenuAnchor(null);
        setSelectedProduct(null);
        showAlert("🗑️ Product deleted successfully!");
        fetchProducts();
      } else {
        showAlert(result.message || "❌ Failed to delete product", "error");
      }
    } catch (error) {
      console.error("❌ Delete error:", error);
      showAlert(`❌ Error deleting product: ${error.message}`, "error");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleMenuOpen = (event, product) => {
    setMenuAnchor(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditClick = () => {
    if (selectedProduct && selectedProduct._id) {
      setEditingProduct(selectedProduct);
      setEditFormData({
        title: selectedProduct.title || "",
        description: selectedProduct.description || "",
        price: selectedProduct.price || "",
        offerPrice: selectedProduct.offerPrice || "",
        stock: selectedProduct.stock || "",
        images: [], // Start with empty array for new images
        removedImages: [], // Initialize correctly
      });
      setEditImagePreviews([]); // Clear any previous previews
      setEditDialogOpen(true);
      setMenuAnchor(null);
    } else {
      showAlert("❌ No product selected for editing", "error");
      setMenuAnchor(null);
    }
  };

  const handleDeleteClick = () => {
    if (selectedProduct && selectedProduct._id) {
      setDeleteDialogOpen(true);
      setMenuAnchor(null);
    } else {
      showAlert("❌ No product selected for deletion", "error");
      setMenuAnchor(null);
    }
  };

  const handleMarkOutOfStock = async (productToUpdate) => {
    const product = productToUpdate || selectedProduct;
    if (!product || !product._id) {
      showAlert("❌ No product selected for stock management", "error");
      setMenuAnchor(null);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/shop/mark-out-of-stock/${product._id}`, {
        method: "PUT",
      });

      const result = await res.json();

      if (result.success) {
        setProducts(prev => prev.map(p => p._id === product._id ? { ...p, inStock: false } : p));
      } else {
        showAlert(result.message || "❌ Failed to mark product as out of stock", "error");
      }
    } catch (error) {
      console.error("❌ Mark out of stock error:", error);
      showAlert(`❌ Error marking product as out of stock: ${error.message}`, "error");
    } finally {
      setMenuAnchor(null);
    }
  };

  const handleRestockProduct = async (productToUpdate) => {
    const product = productToUpdate || selectedProduct;
    if (!product || !product._id) {
      showAlert("❌ No product selected for restocking", "error");
      setMenuAnchor(null);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/shop/restock-product/${product._id}`, {
        method: "PUT",
      });

      const result = await res.json();

      if (result.success) {
        setProducts(prev => prev.map(p => p._id === product._id ? { ...p, inStock: true } : p));
      } else {
        showAlert(result.message || "❌ Failed to restock product", "error");
      }
    } catch (error) {
      console.error("❌ Restock error:", error);
      showAlert(`❌ Error restocking product: ${error.message}`, "error");
    } finally {
      setMenuAnchor(null);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const hasOffer = (product) => {
    return product.offerPrice && product.offerPrice < product.price;
  };

  const renderProductCard = (product) => {
    const imgs =
      product.images && product.images.length > 0
        ? product.images.map((img) => `${API_URL}${img}`)
        : [];
    const idx = imageIndex[product._id] || 0;
    const currentImage = imgs.length > 0 ? imgs[idx] : null;

    return (
      <Card
        sx={{
          width: 280,
          height: 380,
          mx: "auto",
          borderRadius: 4,
          border: "1px solid rgba(128, 128, 128, 0.2)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          transition: "0.3s ease",
          backgroundColor: colors.white,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 16px 32px rgba(0,0,0,0.15)",
          },
        }}
      >


        {/* Image Section with Carousel */}
        <Box
          sx={{
            width: "90%",
            height: 180,
            mt: 2,
            mx: "auto",
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {currentImage ? (
            <>
              <CardMedia
                component="img"
                image={currentImage}
                alt={product.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />

              {/* Carousel Navigation */}
              {imgs.length > 1 && (
                <>
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 8,
                      transform: "translateY(-50%)",
                      color: colors.green,
                      backgroundColor: "rgba(255, 255, 255, 0.25)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.4)",
                        transform: "translateY(-50%) scale(1.15)",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
                        color: colors.darkGreen,
                      },
                      transition: "all 0.3s ease",
                      width: "36px",
                      height: "36px",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage(product._id, imgs.length);
                    }}
                  >
                    <ArrowBackIos
                      fontSize="small"
                      sx={{
                        fontSize: "18px",
                        marginLeft: "2px",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                      }}
                    />
                  </IconButton>

                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 8,
                      transform: "translateY(-50%)",
                      color: colors.green,
                      backgroundColor: "rgba(255, 255, 255, 0.25)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.4)",
                        transform: "translateY(-50%) scale(1.15)",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
                        color: colors.darkGreen,
                      },
                      transition: "all 0.3s ease",
                      width: "36px",
                      height: "36px",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage(product._id, imgs.length);
                    }}
                  >
                    <ArrowForwardIos
                      fontSize="small"
                      sx={{
                        fontSize: "18px",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                      }}
                    />
                  </IconButton>
                </>
              )}
            </>
          ) : (
            <Box
              sx={{
                height: "100%",
                bgcolor: colors.lightGray,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Inventory2
                sx={{ fontSize: 48, color: colors.darkGray, mb: 1 }}
              />
              <Typography variant="body2" color={colors.darkGray}>
                No Image
              </Typography>
            </Box>
          )}
        </Box>



        {/* Card Content */}
        <CardContent
          sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.4,
                fontSize: "0.99rem",
                minHeight: "2.8rem",
                flex: 1,
              }}
            >
              {product.title || "Untitled Product"}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProduct(product);
                setEditingProduct(product);
                setEditFormData({
                  title: product.title || "",
                  description: product.description || "",
                  price: product.price || "",
                  offerPrice: product.offerPrice || "",
                  stock: product.stock || "",
                  images: [],
                  removedImages: [],
                });
                setEditImagePreviews([]);
                setEditDialogOpen(true);
              }}
              sx={{
                color: colors.darkGreen,
                backgroundColor: colors.lightMint,
                padding: "4px",
                "&:hover": {
                  backgroundColor: colors.hoverMint,
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <Edit sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Description moved up */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.4,
              fontSize: "0.8rem",
              mt: -2.5,
              mb: 2,
              flexGrow: 1,
            }}
          >
            {product.description || "No description"}
          </Typography>

          {/* Price and Stock Section */}
          <Box sx={{ mt: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              {hasOffer(product) ? (
                <>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={colors.green}
                  >
                    {formatPrice(product.offerPrice)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.darkGray,
                      textDecoration: "line-through",
                    }}
                  >
                    {formatPrice(product.price)}
                  </Typography>
                </>
              ) : (
                <Typography variant="h6" fontWeight="bold" color={colors.green}>
                  {formatPrice(product.price)}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: -1,
                position: "relative",
                zIndex: 15,
              }}
            >
              <Chip
                icon={
                  product.inStock ? (
                    <Inventory2 sx={{ fontSize: 16, color: colors.darkGreen }} />
                  ) : (
                    <RemoveShoppingCart sx={{ fontSize: 16 }} />
                  )
                }
                label={
                  product.inStock
                    ? `Stock: ${product.stock || 0}`
                    : "Out of Stock"
                }
                size="small"
                color={product.inStock ? "success" : "error"}
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: product.inStock ? "bold" : "bold",
                  backgroundColor: product.inStock ? colors.lightMint : colors.red + "20",
                  border: product.inStock ? "2px solid " + colors.darkGreen : "1px solid " + colors.red,
                  color: product.inStock ? colors.darkGreen : colors.red,
                  position: "relative",
                  zIndex: 15,
                }}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<Delete />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProduct(product);
                setDeleteDialogOpen(true);
              }}
              sx={{
                backgroundColor: colors.red,
                fontWeight: 600,
                borderRadius: 2,
                fontSize: "0.8rem",
                "&:hover": {
                  backgroundColor: colors.darkRed,
                },
              }}
            >
              Delete
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={product.inStock ? <RemoveShoppingCart /> : <Inventory />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProduct(product);
                if (product.inStock) {
                  handleMarkOutOfStock(product);
                } else {
                  handleRestockProduct(product);
                }
              }}
              sx={{
                backgroundColor: product.inStock ? colors.orange : colors.green,
                fontWeight: 600,
                borderRadius: 2,
                fontSize: "0.7rem",
                "&:hover": {
                  backgroundColor: product.inStock ? colors.darkOrange : colors.darkGreen,
                },
              }}
            >
              {product.inStock ? "Out of Stock" : "Restock"}
            </Button>
          </Stack>
        </CardContent>
      </Card >
    );
  };

  const CustomAlert = ({ open, severity, message, onClose }) => {
    if (!open) return null;

    const isSuccess = severity === "success";
    const borderColor = isSuccess ? colors.limeGreen : colors.deleteBg;
    const iconColor = isSuccess ? colors.darkGreen : colors.red;
    const buttonColor = isSuccess ? colors.darkGreen : colors.red;
    const buttonHoverColor = isSuccess ? colors.green : colors.darkRed;

    return (
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          width: "auto",
          minWidth: 400,
          maxWidth: "90vw",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            border: `3px solid ${borderColor}`,
            borderRadius: 4,
            padding: 0,
            boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              backgroundColor: borderColor,
              padding: "16px 24px",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "white",
                flexShrink: 0,
              }}
            >
              {isSuccess ? (
                <CheckCircle sx={{ color: iconColor, fontSize: 22 }} />
              ) : (
                <Cancel sx={{ color: iconColor, fontSize: 22 }} />
              )}
            </Box>

            <Typography
              sx={{
                fontSize: "1.3rem",
                fontWeight: 700,
                color: "white",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {isSuccess ? "Success" : "Error"}
            </Typography>
          </Box>

          <Box
            sx={{
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 500,
                color: colors.darkBlack,
                lineHeight: 1.5,
                textAlign: "center",
              }}
            >
              {message}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: buttonColor,
                  color: "white",
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 4,
                  py: 1,
                  fontSize: "1rem",
                  minWidth: 120,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: buttonHoverColor,
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.darkWhite,
        overflow: "hidden",
      }}
    >
      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />

      {/* Header */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          color: colors.darkBlack,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            minHeight: "64px !important",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              mr: 2,
            }}
          >
            <Tabs
              value={0}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.green,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: "auto",
                  px: { xs: 2, sm: 3 },
                  minHeight: "48px",
                  whiteSpace: "nowrap",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: colors.green,
                },
              }}
            >
              <Tab
                icon={
                  <Inventory2
                    sx={{ mr: 1, fontSize: { xs: "18px", sm: "20px" } }}
                  />
                }
                iconPosition="start"
                label="All Products"
              />
            </Tabs>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              sx={{
                background: colors.darkGreen,
                "&:hover": {
                  background: colors.greenGradientAlt,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(76, 175, 80, 0.3)",
                },
                fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.9rem" },
                padding: { xs: "8px 12px", sm: "10px 20px", md: "11px 22px" },
                minWidth: { xs: "auto", sm: "140px" },
                borderRadius: 4,
                fontWeight: 600,
                transition: "all 0.3s ease",
                whiteSpace: "nowrap",
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Add Product
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "inline", sm: "none" } }}
              >
                Add
              </Box>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Products Content */}
      <Box
        ref={productsContainerRef}
        sx={{
          flex: 1, // CHANGE from flexGrow: 1
          minHeight: 0, // ADD THIS
          overflowY: 'auto',
          overflowX: 'hidden', // ADD THIS
          p: 3,
        }}
      >
        {/* Search Results Info */}
        {searchText && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Search results for: <strong>"{searchText}"</strong>
              {filteredProducts.length > 0 &&
                ` (${filteredProducts.length} found)`}
            </Typography>
          </Box>
        )}

        {/* Loading State with Skeletons */}
        {loading ? (
          <Grid container spacing={4} justifyContent="center">
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <Grid item key={index} xs={12} sm={6} md={4}>
                <SkeletonProductCard />
              </Grid>
            ))}
          </Grid>
        ) : paginatedProducts.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              px: 2,
            }}
          >
            <Inventory2 sx={{ fontSize: 64, color: colors.midGray, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchText
                ? "No products match your search"
                : "No products found"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add products to get started
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4} justifyContent="center">
            {paginatedProducts.map((product) => (
              <Grid item key={product._id} xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "380px",
                    minWidth: "280px",
                    mx: "auto",
                  }}
                >
                  {renderProductCard(product)}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#2c3e50",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                  minWidth: "42px",
                  height: "42px",
                  margin: "0 4px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  },
                },
                "& .MuiPaginationItem-page": {
                  "&.Mui-selected": {
                    backgroundColor: colors.darkGreen,
                    color: colors.white,
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    border: `2px solid ${colors.darkGreen}`,
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      backgroundColor: colors.green,
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                    },
                  },
                },
                "& .MuiPaginationItem-ellipsis": {
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  color: "#666",
                },
                "& .MuiPaginationItem-previousNext": {
                  border: `2px solid ${colors.green}`,
                  backgroundColor: "transparent",
                  color: colors.green,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: colors.green,
                    color: colors.white,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                  },
                  "&.Mui-disabled": {
                    border: "1px solid #e0e0e0",
                    color: "#bdbdbd",
                    backgroundColor: "#fafafa",
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            minWidth: 140,
            overflow: "hidden",
          },
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        {/* Stock Management Options - Only show for admin */}
        {selectedProduct && selectedProduct.inStock ? (
          <MenuItem onClick={handleMarkOutOfStock} sx={{ color: colors.orange }}>
            <ListItemIcon>
              <RemoveShoppingCart fontSize="small" sx={{ color: colors.orange }} />
            </ListItemIcon>
            <ListItemText>Mark Out of Stock</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleRestockProduct} sx={{ color: colors.green }}>
            <ListItemIcon>
              <Inventory fontSize="small" sx={{ color: colors.green }} />
            </ListItemIcon>
            <ListItemText>Restock Product</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleDeleteClick} sx={{ color: colors.red }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: colors.red }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Product Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          if (!dialogLoading) {
            setOpenDialog(false);
            resetForm();
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.lightMint,
            borderBottom: `2px solid ${colors.green}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold" color={colors.darkGreen}>
              Add New Product
            </Typography>
            <IconButton
              onClick={() => {
                if (!dialogLoading) {
                  setOpenDialog(false);
                  resetForm();
                }
              }}
              disabled={dialogLoading}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2} direction="column">
              {/* Product Title */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Product Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  disabled={dialogLoading}
                />
              </Grid>

              {/* Stock Quantity */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Stock Quantity"
                  name="stock"
                  type="number"
                  value={formData.stock}
                  onChange={handleInputChange}
                  required
                  disabled={dialogLoading}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>

              {/* Price */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Price (₹)"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  disabled={dialogLoading}
                  InputProps={{
                    inputProps: { min: 0, step: "0.01" },
                  }}
                />
              </Grid>

              {/* Offer Price */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Offer Price (₹)"
                  name="offerPrice"
                  type="number"
                  value={formData.offerPrice}
                  onChange={handleInputChange}
                  disabled={dialogLoading}
                  InputProps={{
                    inputProps: { min: 0, step: "0.01" },
                  }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  disabled={dialogLoading}
                  placeholder="Enter detailed product description..."
                />
              </Grid>

              {/* Image Upload */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFile />}
                  disabled={dialogLoading || formData.images.length >= 5}
                  sx={{ mb: 2 }}
                >
                  Upload Images
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files);
                      const remainingSlots = 5 - formData.images.length;
                      const selectedFiles = newFiles.slice(0, remainingSlots);
                      handleImageUpload({ target: { files: selectedFiles } });
                    }}
                  />
                </Button>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Selected {formData.images.length}/5 images
                </Typography>

                {formData.images.length >= 5 && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    Maximum 5 images allowed.
                  </Typography>
                )}

                {/* Image Previews */}
                {imagePreviews.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {imagePreviews.map((preview, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        <CardMedia
                          component="img"
                          src={preview}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                            objectFit: "cover",
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            bgcolor: "white",
                            boxShadow: 1,
                          }}
                          onClick={() => removeImage(index)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Buttons */}
            <DialogActions sx={{ px: 0, mt: 3 }}>
              <Button
                onClick={() => {
                  if (!dialogLoading) {
                    setOpenDialog(false);
                    resetForm();
                  }
                }}
                disabled={dialogLoading}
                sx={{ color: colors.darkGray, fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  dialogLoading ||
                  !formData.title ||
                  !formData.price ||
                  !formData.stock
                }
                sx={{
                  background: colors.greenGradient,
                  "&:hover": { background: colors.greenGradientAlt },
                  minWidth: 120,
                  fontWeight: 600,
                }}
              >
                {dialogLoading ? <CircularProgress size={20} /> : "Add Product"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          if (!dialogLoading) {
            setEditDialogOpen(false);
            resetEditForm();
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.lightMint,
            borderBottom: `2px solid ${colors.green}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold" color={colors.darkGreen}>
              Edit Product
            </Typography>
            <IconButton
              onClick={() => {
                if (!dialogLoading) {
                  setEditDialogOpen(false);
                  resetEditForm();
                }
              }}
              disabled={dialogLoading}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Box component="form" onSubmit={handleEditSubmit}>
            <Grid container spacing={2} direction="column">
              {/* Title */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Product Title"
                  name="title"
                  value={editFormData.title}
                  onChange={handleEditInputChange}
                  disabled={dialogLoading}
                  placeholder={editingProduct?.title}
                />
              </Grid>

              {/* Stock */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Stock Quantity"
                  name="stock"
                  type="number"
                  value={editFormData.stock}
                  onChange={handleEditInputChange}
                  disabled={dialogLoading}
                  placeholder={editingProduct?.stock}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>

              {/* Price */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Price (₹)"
                  name="price"
                  type="number"
                  value={editFormData.price}
                  onChange={handleEditInputChange}
                  disabled={dialogLoading}
                  placeholder={editingProduct?.price}
                  InputProps={{
                    inputProps: { min: 0, step: "0.01" },
                  }}
                />
              </Grid>

              {/* Offer Price */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Offer Price (₹)"
                  name="offerPrice"
                  type="number"
                  value={editFormData.offerPrice}
                  onChange={handleEditInputChange}
                  disabled={dialogLoading}
                  placeholder={editingProduct?.offerPrice}
                  helperText="Leave empty if no offer"
                  InputProps={{
                    inputProps: { min: 0, step: "0.01" },
                  }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditInputChange}
                  multiline
                  rows={4}
                  disabled={dialogLoading}
                  placeholder={
                    editingProduct?.description ||
                    "Enter detailed product description..."
                  }
                />
              </Grid>

              {/* Image Section */}
              <Grid item xs={12}>
                {/* Existing Images */}
                {editingProduct?.images?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                      Existing Images:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {editingProduct.images.map((img, index) => (
                        <Box key={index} sx={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            image={`${API_URL}${img}`}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 1,
                              objectFit: "cover",
                              border: `1px solid ${colors.midGray}`,
                            }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              bgcolor: "white",
                              boxShadow: 1,
                              "&:hover": { bgcolor: colors.deleteBg, color: "white" },
                            }}
                            onClick={() => removeExistingImage(img)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFile />}
                  disabled={dialogLoading || editFormData.images.length >= 5}
                  sx={{ mb: 2 }}
                >
                  Add More Images
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files);
                      const remainingSlots = 5 - editFormData.images.length;
                      const selectedFiles = newFiles.slice(0, remainingSlots);
                      handleEditImageUpload({
                        target: { files: selectedFiles },
                      });
                    }}
                  />
                </Button>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {editFormData.images.length}/5 images selected
                </Typography>

                {editFormData.images.length >= 5 && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    Maximum 5 images allowed.
                  </Typography>
                )}

                {/* New Image Previews */}
                {editImagePreviews.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {editImagePreviews.map((preview, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        <CardMedia
                          component="img"
                          src={preview}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                            objectFit: "cover",
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            bgcolor: "white",
                            boxShadow: 1,
                          }}
                          onClick={() => removeEditImage(index)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary">
                  Leave fields empty to keep existing values
                </Typography>
              </Grid>
            </Grid>

            {/* Buttons */}
            <DialogActions sx={{ px: 0, mt: 3 }}>
              <Button
                onClick={() => setEditDialogOpen(false)}
                disabled={dialogLoading}
                sx={{ color: colors.darkGray, fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={dialogLoading}
                sx={{
                  background: colors.greenGradient,
                  "&:hover": { background: colors.greenGradientAlt },
                  minWidth: 120,
                  fontWeight: 600,
                }}
              >
                {dialogLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Update Product"
                )}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!dialogLoading) {
            setDeleteDialogOpen(false);
            setSelectedProduct(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the product "
            {selectedProduct?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedProduct(null);
            }}
            disabled={dialogLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProduct}
            variant="contained"
            disabled={dialogLoading}
            sx={{
              background: colors.red,
              "&:hover": { background: colors.darkRed },
            }}
          >
            {dialogLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopProducts;
